import { TCarriagePlanType } from '../domain/CarriagePlan'
import { getPdadEventCode } from '../domain/PdadEvent'
import {
  PDAD_ARRIVAL,
  PDAD_CARGO_READY,
  PDAD_DELIVERY,
  PDAD_DEPARTURE,
  PDAD_PICKUP,
  PdadStepsSequence,
  TPdadType,
} from '../domain/PdadTypes'
import { TTransportOrCarriageMode } from '../domain/TransportMode'
import { IShipTpEvent } from '../domain/TransportPlanEvent'
import { ITimestampJSON } from '../domain/TransportPlanTimestamp'
import { TTransportType } from '../domain/TransportType'
import { TPdadStepIconType } from '../view/PdadIcon'
import {
  ICarriagePlan,
  IMilestone,
  IMilestoneDelivery,
  IMilestoneDpr,
  IMilestones,
  ITransportPlan2,
  TpPoint,
} from './TransportPlan2'
import { getAllEvents } from './transportPlanEventUtils'
import { getAllMilestones } from './transportPlanMilestoneUtils'

export type PdadStepUI = {
  icon: TPdadStepIconType
  titleTerm: string
  tooltipTerm: string
  estimatedLabelTerm?: string
  locationFallbackTerm?: string
  confirmedLabelTerm?: string
}

export type ProgressPdad = {
  stepUI: Pdad2['stepUI']
  event: IShipTpEvent | undefined
  carriageType: TCarriagePlanType
}

type Pdad2 = {
  milestone?: IMilestone
  point?: TpPoint
  stepUI: PdadStepUI
  transportMode?: TTransportOrCarriageMode | null
}

export type TProgressPdadMap = Partial<Record<TPdadType, ProgressPdad>>
export type TPdadRecord = Partial<Record<TPdadType, Pdad2>>

interface IPdadMilestones {
  pickup: IMilestone | undefined
  departure: IMilestone | undefined
  arrival: IMilestone | undefined
  dpr: IMilestoneDpr | undefined
  delivery: IMilestoneDelivery | undefined
  tsp1: TpPoint | undefined
  tsp2: TpPoint | undefined
  tsp3: TpPoint | undefined
  tsp4: TpPoint | undefined
}

export const getProgressPdad = (
  carriageType: TCarriagePlanType,
  stepUI: PdadStepUI,
  event: IShipTpEvent | undefined
): ProgressPdad => {
  return {
    stepUI,
    event,
    carriageType,
  }
}

// for flex-grow
export const getMainStepsCount = (pdadSequence: ProgressPdad[]): number => {
  return pdadSequence.reduce((mainStepsCount, pdad) => {
    if (pdad.carriageType === 'main') {
      mainStepsCount += 1
    }
    return mainStepsCount
  }, 0)
}

const getPickup = (milestones: IMilestones) => milestones.pickup
const getDeparture = (milestones: IMilestones) => milestones.pol || milestones.departureAirport || milestones.departure
const getArrival = (milestones: IMilestones) => milestones.pod || milestones.destinationAirport || milestones.arrival
const getDelivery = (milestones: IMilestones) => milestones.delivery
const getDpr = (milestones: IMilestones) => milestones.por

export const getTransshipmentPort = (plan: ICarriagePlan, tspStepNumber: number) =>
  (plan &&
    plan.route &&
    plan.route.segments &&
    plan.route.segments.length > tspStepNumber &&
    plan.route.segments[tspStepNumber - 1].journey.destination) ||
  undefined

export const getPdadMilestones = (milestones: IMilestones, main: ICarriagePlan): IPdadMilestones => {
  const pickup = getPickup(milestones)
  const departure = getDeparture(milestones)
  const arrival = getArrival(milestones)
  const dpr = getDpr(milestones)
  const delivery = getDelivery(milestones)
  const tsp1 = getTransshipmentPort(main, 1)
  const tsp2 = getTransshipmentPort(main, 2)
  const tsp3 = getTransshipmentPort(main, 3)
  const tsp4 = getTransshipmentPort(main, 4)
  return { pickup, dpr, departure, arrival, delivery, tsp1, tsp2, tsp3, tsp4 }
}

export const getPdadEvents = (
  pdadMilestones: Partial<Record<TPdadType, IMilestone | undefined>>,
  events: Record<string, IShipTpEvent>,
  transportType: TTransportType,
  transportMode: TTransportOrCarriageMode
): Record<TPdadType, IShipTpEvent | undefined> => {
  return {
    cargoReady: events[getPdadEventCode(PDAD_CARGO_READY, transportType, transportMode)],
    pickup: pdadMilestones.pickup && events[getPdadEventCode(PDAD_PICKUP, transportType, transportMode)],
    departure: pdadMilestones.departure && events[getPdadEventCode(PDAD_DEPARTURE, transportType, transportMode)],
    arrival: pdadMilestones.arrival && events[getPdadEventCode(PDAD_ARRIVAL, transportType, transportMode)],
    delivery: pdadMilestones.delivery && events[getPdadEventCode(PDAD_DELIVERY, transportType, transportMode)],
    dpr: pdadMilestones.dpr && events.dpr,
    tsp1: pdadMilestones.tsp1 && events.vdt1,
    tsp2: pdadMilestones.tsp2 && events.vdt2,
    tsp3: pdadMilestones.tsp3 && events.vdt3,
    tsp4: pdadMilestones.tsp4 && events.vdt4,
    cgomd: events.cgomd,
    cgomo: events.cgomo,
  }
}

export const getContainersEvents = (tp: ITransportPlan2) => {
  const containerCount: number = tp.main ? tp.main.length : 0

  if (containerCount < 1) {
    return null
  }

  const containersEvents: {
    pdadType: TPdadType
    event: IShipTpEvent | undefined
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
  }[][] = tp.main.map((container, index) => {
    // @ts-expect-error TS(2322) FIXME: Type 'ICarriagePlan | undefined' is not assignable... Remove this comment to see the full error message
    const pre: ICarriagePlan = tp.pre && tp.pre[index]
    // @ts-expect-error TS(2322) FIXME: Type 'ICarriagePlan | undefined' is not assignable... Remove this comment to see the full error message
    const main: ICarriagePlan = tp.main && tp.main[index]
    // @ts-expect-error TS(2322) FIXME: Type 'ICarriagePlan | undefined' is not assignable... Remove this comment to see the full error message
    const on: ICarriagePlan = tp.on && tp.on[index]
    const milestones: IMilestones = getAllMilestones([pre, main, on])
    const pdadMilestones: IPdadMilestones = getPdadMilestones(milestones, main)
    const events: Record<string, IShipTpEvent> = getAllEvents([pre, main, on])
    const pdadEvents: Record<TPdadType, IShipTpEvent | undefined> = getPdadEvents(
      pdadMilestones,
      events,
      tp.transportType,
      tp.transportMode
    )
    return PdadStepsSequence.filter((step) => !step.match(/^tsp/)).map((step) => ({
      pdadType: step as TPdadType,
      event: pdadEvents[step],
    }))
  })

  return containersEvents.reduce(
    (result, events) => {
      events.forEach(({ pdadType, event }) => {
        if (!result[pdadType]) {
          result[pdadType] = []
        }

        event && result[pdadType].push(event)
      })
      return result
    },
    {} as Record<TPdadType, IShipTpEvent[]>
  )
}

export const getContainersCargoReady = (transportPlan: ITransportPlan2): IShipTpEvent[] => {
  const crdEventName = getPdadEventCode(PDAD_CARGO_READY, transportPlan.transportType, transportPlan.transportMode)
  const crdEvents = transportPlan.origin?.map(({ events }) => events[crdEventName])

  return crdEvents?.filter(Boolean) ?? []
}

export interface ITransportPlanInformation {
  tp?: ITransportPlan2
  pdadMap?: TProgressPdadMap
  pdadSequence?: ProgressPdad[]
  pdadDeliveryDate?: string
  pdadDeliveryTimestamp?: ITimestampJSON
  pdadDeliveryEvents?: IShipTpEvent[]
}
